import { profileState } from "@/atoms/user/profile-state";
import { LOCAL_STORAGE } from "@/common/constant/local-storage";
import { ButtonColor } from "@/common/enum/button-color";
import { CheckboxColor } from "@/common/enum/checkbox-color";
import { LoginPath, SignUpPath } from "@/common/enum/path";
import { TextInputColor } from "@/common/enum/text-input-color";
import { removeAccessToken } from "@/common/util/remove-access-token";
import { PasswordEyeIcon } from "@/components/icon/PasswordEyeIcon";
import { TextInputLabel } from "@/components/label/TextInputLabel";
import { Body14Medium } from "@/components/typography/Body14Medium";
import { Body14Strong } from "@/components/typography/Body14Strong";
import { Body14Underline } from "@/components/typography/Body14Underline";
import { Body16Medium } from "@/components/typography/Body16Medium";
import { H4 } from "@/components/typography/H4";
import { authService } from "@/services/auth-service";
import { userService } from "@/services/user-service";
import { LoginPayload } from "@/types/payload/sign-in-payload";
import { Button, Checkbox, TextInput } from "flowbite-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";

const initLoginPayload: LoginPayload = {
  email: "",
  password: "",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [loginPayload, setLoginPayload] = useState(initLoginPayload);
  const [isRememberEmail, setIsRememberEmail] = useState(false);
  const [isKeepLoggedIn, setIsKeepLoggedIn] = useState(false);
  const [isError, setIsError] = useState(false);
  const setProfile = useSetRecoilState(profileState);
  const [showPassword, setShowPassword] = useState(false);
  const isLoginButtonActive = loginPayload.email && loginPayload.password;

  const handleSignUpPageButtonClick = () => {
    navigate(SignUpPath.SIGN_UP);
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLoginPayloadChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setLoginPayload({
      ...loginPayload,
      [name]: value,
    });
  };

  const handleRememberEmailChange = () => {
    if (!isRememberEmail) {
      localStorage.setItem(LOCAL_STORAGE.REMEMBERED_EMAIL, loginPayload.email);
    } else {
      localStorage.removeItem(LOCAL_STORAGE.REMEMBERED_EMAIL);
    }

    setIsRememberEmail(!isRememberEmail);
  };

  const handleKeepLoggedInChange = () => {
    setIsKeepLoggedIn(!isKeepLoggedIn);
  };

  const handleLoginButtonClick = async () => {
    try {
      if (isRememberEmail) {
        localStorage.setItem(
          LOCAL_STORAGE.REMEMBERED_EMAIL,
          loginPayload.email
        );
      } else {
        localStorage.removeItem(LOCAL_STORAGE.REMEMBERED_EMAIL);
      }
      const loginResponse = await authService.login(loginPayload);
      if (!loginResponse.ok) {
        console.log(loginResponse);
        setIsError(true);
        removeAccessToken();
        return;
      }
      const { access_token } = loginResponse.data;
      if (isKeepLoggedIn) {
        localStorage.setItem("accessToken", access_token);
      } else {
        sessionStorage.setItem("accessToken", access_token);
      }
      const getMeResponse = await userService.getMe();
      if (!getMeResponse.ok) {
        console.log(getMeResponse);
        setIsError(true);
        removeAccessToken();
        return;
      }
      setProfile(getMeResponse.data);
    } catch (error) {
      console.log(error);
      setIsError(true);
      removeAccessToken();
    }
  };

  const handleFindIdAndPasswordClick = () => {
    navigate(LoginPath.FIND_ID_AND_PASSWORD);
  };

  useEffect(() => {
    const savedEmail = localStorage.getItem(LOCAL_STORAGE.REMEMBERED_EMAIL);
    if (savedEmail) {
      setLoginPayload((prevPayload) => ({
        ...prevPayload,
        email: savedEmail,
      }));
      setIsRememberEmail(true);
    }
  }, []);

  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-center min-h-screen w-screen h-screen">
      <div className="py-10 min-w-[440px] lg:h-full lg:flex-1 bg-gradient-to-br from-[#000000] to-[#1F3D73]  w-full flex flex-col items-center justify-center">
        <div className="flex flex-col justify-center items-center mb-8">
          <p className="text-h4 lg:text-h3 text-white font-medium">
            아카라 사업자 전용 발주몰만의
          </p>
          <h1 className="text-h2 lg:text-h1 text-white font-semibold">
            다양한 혜택, 편리한 기능
          </h1>
        </div>
        <img
          src="/img/login-side.png"
          alt="광고1"
          className="w-[300px] lg:w-[440px] h-auto rounded-lg shadow"
        />
      </div>
      <div className="lg:flex-1 flex justify-center items-center flex-col">
        <div className="min-w-[400px]">
          <div className="flex flex-col my-4 ">
            <div className="flex items-center justify-center">
              <img
                src="/img/logo_black.png"
                alt="Logo"
                className="w-[129px] h-[30px]"
              />
              <H4 className="text-semantic-text-heading m-2">
                사업자 전용 발주몰
              </H4>
            </div>
            <p className="text-[13px] text-center text-semantic-text-tertiary">
              사업자등록번호로 회원가입 후 이용하실 수 있습니다.
            </p>
          </div>

          <TextInputLabel required>아이디</TextInputLabel>
          <TextInput
            required
            className="mb-[24px]"
            placeholder="aqara@gmail.com"
            name="email"
            color={isError ? TextInputColor.FAILURE : TextInputColor.GRAY}
            value={loginPayload.email}
            onChange={handleLoginPayloadChange}
          />
          <TextInputLabel required>비밀번호</TextInputLabel>
          <TextInput
            required
            className="mb-[24px]"
            placeholder="비밀번호를 입력해주세요"
            name="password"
            type={showPassword ? "text" : "password"}
            color={isError ? TextInputColor.FAILURE : TextInputColor.GRAY}
            rightIcon={() => {
              return (
                <PasswordEyeIcon
                  showPassword={showPassword}
                  isError={isError}
                  onClick={toggleShowPassword}
                />
              );
            }}
            value={loginPayload.password}
            onChange={handleLoginPayloadChange}
          />
          <div className="flex justify-between">
            <div className="flex">
              <div className="flex items-center">
                <Checkbox
                  className="mr-[8px] text-semantic-text-heading"
                  checked={isKeepLoggedIn}
                  color={isKeepLoggedIn ? CheckboxColor.DARK : ""}
                  onChange={handleKeepLoggedInChange}
                />
                <Body14Medium>로그인 유지</Body14Medium>
              </div>
              <div className="flex items-center ml-[12px]">
                <Checkbox
                  className="mr-[8px] text-semantic-text-heading"
                  checked={isRememberEmail}
                  color={isRememberEmail ? CheckboxColor.DARK : ""}
                  onChange={handleRememberEmailChange}
                />
                <Body14Medium>아이디 기억하기</Body14Medium>
              </div>
            </div>
            <div
              className="text-gray-800 cursor-pointer"
              onClick={handleFindIdAndPasswordClick}
            >
              <Body14Strong>아이디/비밀번호 찾기</Body14Strong>
            </div>
          </div>
          {isError && (
            <div className="flex items-center justify-center">
              <Body16Medium className="text-semantic-text-error-primary mt-[24px]">
                아이디 혹은 비밀번호가 일치하지 않습니다.
              </Body16Medium>
            </div>
          )}
          <Button
            className="mt-[40px] mb-[24px] w-full"
            color={isLoginButtonActive && ButtonColor.PRIMARY}
            disabled={!isLoginButtonActive}
            onClick={handleLoginButtonClick}
          >
            로그인
          </Button>
          <div className="flex items-center justify-center text-semantic-text-body mb-4">
            <Body14Medium>아이디가 없으신가요?</Body14Medium>
            <div
              className="ml-[4px] cursor-pointer"
              onClick={handleSignUpPageButtonClick}
            >
              <Body14Underline>회원가입 하기</Body14Underline>
            </div>
          </div>
          <div className="flex items-center justify-center text-semantic-text-body">
            <Body14Medium>
              발주물 소식과 각종 혜택은{" "}
              {
                <a
                  href="https://aqaralife.gitbook.io/partner-guide/intro/notification/2024.12.16"
                  className="underline"
                >
                  공지사항
                </a>
              }
              에서 확인하세요.
            </Body14Medium>
          </div>
        </div>
      </div>
    </div>
  );
}
